import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dateLocale } from '../../i18n/locale';
import { localize } from '../../i18n/script';
import type { UserBookingsResponse } from '../../services/bookingApi';

interface CancelBookingModalProps {
  booking: UserBookingsResponse;
  onClose: () => void;
  onConfirm: (refundMethod: 'STORE_CREDIT' | 'STRIPE_REFUND') => Promise<void>;
}

export default function CancelBookingModal({
  booking,
  onClose,
  onConfirm,
}: CancelBookingModalProps) {
  const { t, i18n } = useTranslation(['booking']);
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [refundMethod, setRefundMethod] = useState<'STORE_CREDIT' | 'STRIPE_REFUND'>(
    'STORE_CREDIT'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPassBooking = !!booking.userPassId;
  const isHostCancelled = booking.status.toUpperCase() === 'CANCELLED_BY_HOST';
  const totalAmount = booking.totalAmount;
  const storeCreditApplied = booking.storeCreditApplied ?? 0;
  const stripeAmount = totalAmount - storeCreditApplied;

  // Calculate refund amounts
  const creditRefundAmount = totalAmount; // 100% to credit
  // Host-cancelled: 100% Stripe refund (no 5% fee); user-initiated: 95%
  const stripeRefundAmount = isHostCancelled ? stripeAmount : Math.floor(stripeAmount * 0.95);
  const creditReturnForStripe = storeCreditApplied; // Credit portion returned 100%

  // Calculate capacity count (same logic as backend getCapacityCount)
  const kidsCount = booking.participants.filter((p) => p.childProfileId).length;
  const adultsCount = booking.participants.filter((p) => p.adultProfileId).length;
  const activity = booking.session.activity;
  const capacityCount =
    (activity.childCountsTowardCapacity ? kidsCount : 0) +
    (activity.adultCountsTowardCapacity ? adultsCount : 0);

  const activityTitle = localize(
    booking.session.activity.titleCN,
    booking.session.activity.titleEN,
    i18n.language
  );

  const sessionDate = new Date(booking.session.startTime).toLocaleDateString(
    dateLocale(i18n.language),
    {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }
  );

  // Stripe option disabled if no Stripe payment was made
  const canStripeRefund = stripeAmount > 0;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm(refundMethod);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel booking');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {isHostCancelled ? t('cancelModal.hostCancelledTitle') : t('cancelModal.title')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          {step === 'select' ? (
            <>
              {/* Activity info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">{activityTitle}</p>
                <p className="text-sm text-gray-500">{sessionDate}</p>
                {isPassBooking ? (
                  <p className="text-sm text-gray-500">{t('cancelModal.passBookingInfo')}</p>
                ) : (
                  <p className="text-sm text-gray-500">
                    {t('cancelModal.totalPaid')}: ${(totalAmount / 100).toFixed(2)}
                  </p>
                )}
              </div>

              {/* Host-cancelled banner */}
              {isHostCancelled && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800 font-medium">
                    {t('cancelModal.hostCancelledMessage')}
                  </p>
                </div>
              )}

              {isPassBooking ? (
                /* Pass booking: show restore info */
                <div className="p-3 border border-blue-500 bg-blue-50 rounded-lg">
                  <p className="font-medium text-gray-900">{t('cancelModal.passRefundMethod')}</p>
                  <p className="text-sm text-green-600 font-medium mt-1">
                    {t('cancelModal.passRefundDesc', { count: capacityCount })}
                  </p>
                </div>
              ) : (
                <>
                  {/* Refund method selection */}
                  <p className="text-sm text-gray-700 mb-3">
                    {t('cancelModal.selectRefundMethod')}
                  </p>

                  <div className="space-y-3">
                    {/* Store Credit option */}
                    <label
                      className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                        refundMethod === 'STORE_CREDIT'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="refundMethod"
                        value="STORE_CREDIT"
                        checked={refundMethod === 'STORE_CREDIT'}
                        onChange={() => setRefundMethod('STORE_CREDIT')}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <p className="font-medium text-gray-900">
                          {t('cancelModal.storeCreditOption')}
                        </p>
                        <p className="text-sm text-green-600 font-medium">
                          ${(creditRefundAmount / 100).toFixed(2)} {t('cancelModal.toCredit')}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('cancelModal.storeCreditDesc')}
                        </p>
                      </div>
                    </label>

                    {/* Stripe refund option */}
                    <label
                      className={`flex items-start p-3 border rounded-lg transition-colors ${
                        canStripeRefund
                          ? refundMethod === 'STRIPE_REFUND'
                            ? 'border-blue-500 bg-blue-50 cursor-pointer'
                            : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                          : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                      }`}
                    >
                      <input
                        type="radio"
                        name="refundMethod"
                        value="STRIPE_REFUND"
                        checked={refundMethod === 'STRIPE_REFUND'}
                        onChange={() => setRefundMethod('STRIPE_REFUND')}
                        disabled={!canStripeRefund}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <p className="font-medium text-gray-900">
                          {t('cancelModal.stripeRefundOption')}
                        </p>
                        {canStripeRefund ? (
                          <>
                            <p className="text-sm text-gray-700">
                              ${(stripeRefundAmount / 100).toFixed(2)} {t('cancelModal.toOriginal')}
                              {creditReturnForStripe > 0 && (
                                <span>
                                  {' '}
                                  + ${(creditReturnForStripe / 100).toFixed(2)}{' '}
                                  {t('cancelModal.toCredit')}
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {isHostCancelled
                                ? t('cancelModal.stripeRefundDescHostCancelled')
                                : t('cancelModal.stripeRefundDesc')}
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-gray-500">
                            {t('cancelModal.stripeRefundDisabled')}
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </>
              )}

              <div className="mt-4 flex gap-3">
                {!isHostCancelled && (
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {t('cancelModal.cancel')}
                  </button>
                )}
                <button
                  onClick={() => setStep('confirm')}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  {t('cancelModal.next')}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Confirmation step */}
              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-3">{t('cancelModal.confirmMessage')}</p>
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t('cancelModal.activity')}</span>
                    <span className="font-medium">{activityTitle}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t('cancelModal.dateTime')}</span>
                    <span className="font-medium">{sessionDate}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{t('cancelModal.refundMethodLabel')}</span>
                    <span className="font-medium">
                      {isPassBooking
                        ? t('cancelModal.passRefundMethod')
                        : refundMethod === 'STORE_CREDIT'
                          ? t('cancelModal.storeCreditOption')
                          : t('cancelModal.stripeRefundOption')}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-2">
                    <span className="text-gray-500">{t('cancelModal.refundAmount')}</span>
                    <span className="font-medium text-green-600">
                      {isPassBooking
                        ? t('cancelModal.passRefundDesc', { count: capacityCount })
                        : refundMethod === 'STORE_CREDIT'
                          ? `$${(creditRefundAmount / 100).toFixed(2)}`
                          : `$${(stripeRefundAmount / 100).toFixed(2)}${creditReturnForStripe > 0 ? ` + $${(creditReturnForStripe / 100).toFixed(2)} credit` : ''}`}
                    </span>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-3 p-2 bg-red-50 text-red-600 text-sm rounded">{error}</div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('select')}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('cancelModal.back')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {isSubmitting ? t('cancelModal.cancelling') : t('cancelModal.confirmCancel')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
